<template>
    <div class="lucky-fullscreen-stage">
        <!-- Theatrical Stage Spotlights & Auroras -->
        <div class="stage-effects" aria-hidden="true">
            <div class="spotlight spotlight-top"></div>
            <div class="spotlight spotlight-left"></div>
            <div class="spotlight spotlight-right"></div>
            <div class="aurora aurora-1"></div>
            <div class="aurora aurora-2"></div>
            <div class="aurora aurora-3"></div>

            <!-- Full-Screen Falling Confetti Stream (when winner is revealed) -->
            <div class="confetti-wrapper" v-if="winnerReady">
                <span v-for="n in 35" :key="`confetti-${n}`" :class="`confetti confetti-${n}`"></span>
            </div>

            <!-- Floating Stage Sparkles & Balloons -->
            <span class="balloon balloon-1"></span>
            <span class="balloon balloon-2"></span>
            <span class="balloon balloon-3"></span>
            <span class="balloon balloon-4"></span>
            <span class="sparkle-star star-1">✦</span>
            <span class="sparkle-star star-2">★</span>
            <span class="sparkle-star star-3">✦</span>
            <span class="sparkle-star star-4">★</span>
            <span class="sparkle-star star-5">✦</span>
        </div>

        <!-- Full-Screen Stage Frame (Flex Column, 100vh, No Scroll) -->
        <div class="fullscreen-container" role="dialog" aria-modal="true">
            <!-- 1. Top Event Header Bar -->
            <header class="event-header-row">
                <div class="event-brand-group">
                    <div class="event-live-pill">
                        <span class="live-pulse-dot"></span>
                        <span class="pill-text">✨ VSDIGI GRAND EVENT 2026</span>
                    </div>
                    <h1 class="event-stage-title">
                        <span class="gold-gradient-text">LUCKY DRAW</span>
                        <span class="stage-sub-badge">OFFICIAL WINNER REVEAL</span>
                    </h1>
                    <p class="draw-date-range">
                        <i class="fa fa-calendar-alt" aria-hidden="true"></i>
                        <span v-if="drawFromDate || drawToDate">
                            From: {{ formatRegistrationDate(drawFromDate) || 'Any date' }}
                            &nbsp; To: {{ formatRegistrationDate(drawToDate) || 'Any date' }}
                        </span>
                        <span v-else>Time frame: All dates</span>
                    </p>
                </div>

                <div class="event-header-actions">
                    <button
                        v-if="winnerReady && !saving"
                        class="stage-header-btn redraw-btn"
                        @click="restart"
                        title="Draw Another Winner"
                    >
                        <i class="fa fa-sync-alt"></i>
                        <span>Draw Again</span>
                    </button>
                    <button
                        class="stage-header-btn close-btn"
                        @click="goBack"
                        title="Close Screen"
                        aria-label="Close"
                    >
                        <i class="fa fa-times"></i>
                    </button>
                </div>
            </header>

            <!-- 2. Main Stage Center Section -->
            <!-- A. Suspense Draw Animation State (10-Second Suspense) -->
            <main v-if="!winnerReady" class="suspense-stage-content">
                <div class="lottery-drum-wrapper">
                    <div class="drum-orbit-ring ring-1"></div>
                    <div class="drum-orbit-ring ring-2"></div>
                    <div class="drum-orbit-ring ring-3"></div>

                    <div class="jackpot-reel-card">
                        <div class="reel-top-tag">
                            <span class="jackpot-icon">🎰</span>
                            <span>SHUFFLING CANDIDATE TICKETS</span>
                        </div>

                        <div class="rolling-name-container">
                            <h2 class="rolling-customer-name">{{ rollingCustomerText }}</h2>
                        </div>

                        <div class="rolling-id-badge">
                            <i class="fa fa-ticket-alt"></i>
                            <span>TICKET: {{ rollingIdText }}</span>
                        </div>
                    </div>
                </div>

                <div class="suspense-bottom-panel">
                    <div class="suspense-countdown-badge">
                        <i class="fa fa-stopwatch"></i>
                        <span>REVEALING IN: {{ countdownSeconds }}s</span>
                    </div>

                    <h3 class="suspense-status-heading">
                        <span class="shimmer-text">Selecting The Grand Winner...</span>
                    </h3>

                    <div class="event-progress-track">
                        <div class="event-progress-fill" :style="{ width: `${animationProgress}%` }"></div>
                    </div>

                    <p class="suspense-subtext">{{ statusText }}</p>
                </div>

                <div v-if="apiError" class="stage-error-banner">
                    <i class="fa fa-exclamation-triangle"></i>
                    <span>{{ apiError }}</span>
                    <button class="stage-retry-btn" @click="startDraw">Retry Draw</button>
                </div>
            </main>

            <!-- B. Grand Winner Revealed State -->
            <main v-else class="winner-revealed-content">
                <!-- Grand Winner Hero Showcase (Prominent & High-Impact) -->
                <section class="winner-spotlight-card">
                    <div class="spotlight-aura"></div>

                    <!-- Winner Crown or User Image Avatar -->
                    <div class="crown-badge-col">
                        <div class="winner-avatar-wrap" v-if="hasValidWinnerImage">
                            <img
                                :src="winnerImageUrl"
                                alt="Winner Photo"
                                class="winner-avatar-img"
                                @error="imageLoadError = true"
                            />
                            <span class="avatar-crown-overlay">👑</span>
                        </div>
                        <div class="crown-orb" v-else>
                            <span class="crown-emoji">👑</span>
                        </div>
                    </div>

                    <div class="winner-info-col">
                        <div class="winner-headline-tags">
                            <span class="grand-position-pill">
                                🥇 Lucky Winner
                            </span>
                            <span v-if="generatedIdValue || winnerId" class="grand-id-pill">
                                <i class="fa fa-id-card"></i> ID: {{ generatedIdValue || winnerId }}
                            </span>
                        </div>

                        <h2 class="grand-winner-name">{{ winnerName }}</h2>

                        <div class="winner-quick-pills">
                            <div class="contact-pill" v-if="winnerMobile">
                                <i class="fa fa-phone-alt"></i>
                                <span>{{ winnerMobile }}</span>
                            </div>
                            <div class="contact-pill muted" v-if="winnerRegistrationDate">
                                <i class="fa fa-calendar-check"></i>
                                <span>Registered: {{ formatRegistrationDate(winnerRegistrationDate) }}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Detailed Information 2-Column Split -->
                <section class="details-section-grid">
                    <!-- Customer Information Card -->
                    <div class="stage-info-card">
                        <div class="info-card-header">
                            <div class="info-card-icon"><i class="fa fa-user-check"></i></div>
                            <h3>Customer Profile</h3>
                        </div>
                        <div class="info-card-list">
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-id-badge"></i> Customer ID:</span>
                                <strong class="row-value gold-highlight">{{ generatedIdValue || 'N/A' }}</strong>
                            </div>
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-mobile-alt"></i> Mobile Number:</span>
                                <strong class="row-value">{{ winnerMobile || 'N/A' }}</strong>
                            </div>
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-map-marker-alt"></i> Full Address:</span>
                                <strong class="row-value text-ellipsis" :title="addressValue">{{ addressValue || 'N/A' }}</strong>
                            </div>
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-calendar-alt"></i> Registration Date:</span>
                                <strong class="row-value">{{ formatRegistrationDate(winnerRegistrationDate) || 'N/A' }}</strong>
                            </div>
                        </div>
                    </div>

                    <!-- Parent / Introducer Card -->
                    <div class="stage-info-card">
                        <div class="info-card-header">
                            <div class="info-card-icon parent-icon"><i class="fa fa-sitemap"></i></div>
                            <h3>Introducer / Parent Details</h3>
                        </div>
                        <div class="info-card-list">
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-user-tag"></i> Parent ID:</span>
                                <strong class="row-value cyan-highlight">{{ parentIdValue || 'N/A' }}</strong>
                            </div>
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-user"></i> Parent Name:</span>
                                <strong class="row-value">{{ parentNameValue || 'N/A' }}</strong>
                            </div>
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-phone"></i> Parent Mobile:</span>
                                <strong class="row-value">{{ winnerParentMobile || 'N/A' }}</strong>
                            </div>
                            <div class="info-grid-row">
                                <span class="row-label"><i class="fa fa-map-signs"></i> Parent Address:</span>
                                <strong class="row-value text-ellipsis" :title="parentAddressValue">{{ parentAddressValue || 'N/A' }}</strong>
                            </div>
                        </div>
                    </div>

                    <!-- Sale Strip (when sales flow) -->
                    <div v-if="saleRecord" class="stage-sale-strip span-full">
                        <div class="sale-metric">
                            <span class="metric-lbl">SALE DATE:</span>
                            <strong class="metric-val">{{ formatRegistrationDate(saleRecord.transactiondate) || 'N/A' }}</strong>
                        </div>
                        <div class="sale-metric">
                            <span class="metric-lbl">LAN NUMBER:</span>
                            <strong class="metric-val">{{ saleManualId || 'N/A' }}</strong>
                        </div>
                        <div class="sale-metric gold-metric">
                            <span class="metric-lbl">TOTAL SALE VALUE:</span>
                            <strong class="metric-val">₹ {{ saleValue }}</strong>
                        </div>
                        <div class="sale-items-pill-group" v-if="saleItems.length">
                            <span v-for="(item, i) in saleItems.slice(0, 4)" :key="i" class="product-item-chip">
                                <i class="fa fa-box"></i> {{ item.productName }} (Qty: {{ item.quantity }})
                            </span>
                        </div>
                    </div>
                </section>
            </main>

            <!-- 3. Bottom Action & Reward Bar (Clean & Elevated) -->
            <footer class="stage-bottom-bar" v-if="winnerReady">
                <div class="reward-entry-container">
                    <div class="reward-input-label">
                        <i class="fa fa-gift"></i>
                        <span>Winner Reward / Prize Note:</span>
                    </div>
                    <input
                        type="text"
                        class="stage-reward-input"
                        maxlength="255"
                        :disabled="saving || !!saveSuccess"
                        v-model="remark"
                        placeholder="Type prize description (e.g. 55-inch 4K Smart LED TV, Gold Coin 10g, Gift Hamper)..."
                        @keyup.enter="saveLuckyWinnerRecord"
                    />
                </div>

                <div class="stage-action-btns">
                    <button
                        class="stage-btn save-winner-btn"
                        :disabled="saving || !!saveSuccess"
                        @click="saveLuckyWinnerRecord"
                    >
                        <i v-if="saving" class="fa fa-spinner fa-spin"></i>
                        <i v-else class="fa fa-check-circle"></i>
                        <span>{{ saving ? 'Saving Record...' : 'Confirm & Save Winner' }}</span>
                    </button>
                    <button
                        class="stage-btn redraw-stage-btn"
                        :disabled="saving"
                        @click="restart"
                    >
                        <i class="fa fa-redo"></i>
                        <span>Draw Next Winner</span>
                    </button>
                </div>
            </footer>

            <!-- Notification Overlays -->
            <div v-if="saveSuccess" class="stage-toast toast-success">
                <i class="fa fa-check-circle"></i> {{ saveSuccess }}
            </div>
            <div v-if="apiError" class="stage-toast toast-error">
                <i class="fa fa-exclamation-circle"></i> {{ apiError }}
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import Constants from '../../components/utilities/Constants.vue'

export default {
    mixins: [Constants],
    props: {
        searchParams: {
            type: Object,
            default: null,
        },
        winnerPayload: {
            type: Object,
            default: null,
        },
        flowType: {
            type: String,
            default: 'customer',
        },
    },
    data() {
        return {
            payload: null,
            position: null,
            winnerReady: false,
            remark: '',
            saving: false,
            saveSuccess: '',
            apiError: '',
            statusText: 'Shuffling eligible candidate tickets across all entries...',
            animationProgress: 0,
            countdownSeconds: 10,
            shuffleTimer: null,
            progressTimer: null,
            countdownTimer: null,
            imageLoadError: false,
            mockCustomers: [
                { name: 'Amitabh Sharma', id: 'VSD-10928' },
                { name: 'Rajesh Verma', id: 'VSD-22041' },
                { name: 'Sunita Devi', id: 'VSD-30914' },
                { name: 'Manoj Kumar', id: 'VSD-41829' },
                { name: 'Pooja Agarwal', id: 'VSD-52718' },
                { name: 'Vikram Singh', id: 'VSD-63102' },
                { name: 'Anjali Gupta', id: 'VSD-74291' },
                { name: 'Ramesh Patel', id: 'VSD-85304' },
                { name: 'Kavita Joshi', id: 'VSD-96417' },
                { name: 'Deepak Rao', id: 'VSD-18234' },
            ],
            shuffleIndex: 0,
            fetchedWinnerData: null,
        }
    },
    computed: {
        drawFromDate() {
            const params = this.searchParams || {}
            return this.flowType === 'sales' ? params.fromdate : params.registation_datefrom
        },
        drawToDate() {
            const params = this.searchParams || {}
            return this.flowType === 'sales' ? params.todate : params.registation_dateto
        },
        rollingCustomerText() {
            return this.mockCustomers[this.shuffleIndex % this.mockCustomers.length].name
        },
        rollingIdText() {
            return this.mockCustomers[this.shuffleIndex % this.mockCustomers.length].id
        },
        winnerRecord() {
            return this.payload?.selecteduser
                || this.payload?.customer
                || this.payload?.winner
                || this.payload?.user
                || this.payload
                || null
        },
        winnerName() {
            return this.winnerRecord?.name || 'Lucky Winner'
        },
        winnerImageUrl() {
            const img = this.winnerRecord?.profileimage
                || this.winnerRecord?.image
                || this.winnerRecord?.avatar
                || this.winnerRecord?.photo
                || this.winnerRecord?.profile_image

            if (!img) {
                return null
            }
            if (typeof img === 'string') {
                return img.startsWith('http') || img.startsWith('/') ? img : `/${img}`
            }
            if (typeof img === 'object') {
                if (img.fullurl) return img.fullurl
                if (img.url) return img.url
                if (img.path) return img.path.startsWith('http') || img.path.startsWith('/') ? img.path : `/${img.path}`
            }
            return null
        },
        hasValidWinnerImage() {
            return !this.imageLoadError && !!this.winnerImageUrl
        },
        winnerId() {
            return this.winnerRecord?.generatedid || this.winnerRecord?.id || ''
        },
        generatedIdValue() {
            return this.winnerRecord?.generatedid || ''
        },
        winnerRegistrationDate() {
            return this.winnerRecord?.registation_date || ''
        },
        winnerMobile() {
            const mobileList = Array.isArray(this.winnerRecord?.mobiles)
                ? this.winnerRecord.mobiles.map(item => item?.mobile).filter(Boolean)
                : []

            if (mobileList.length > 0) {
                return mobileList.join(', ')
            }

            return this.winnerRecord?.mobiles?.[0]?.mobile
                || this.winnerRecord?.mobile?.mobile
                || this.winnerRecord?.mobileno
                || this.winnerRecord?.mobile
                || ''
        },
        winnerParentMobile() {
            const mobileList = Array.isArray(this.winnerRecord?.referraluser?.mobiles)
                ? this.winnerRecord?.referraluser?.mobiles.map(item => item?.mobile).filter(Boolean)
                : []

            if (mobileList.length > 0) {
                return mobileList.join(', ')
            }

            return this.winnerRecord?.referraluser?.mobiles?.[0]?.mobile
                || this.winnerRecord?.referraluser?.mobile?.mobile
                || this.winnerRecord?.referraluser?.mobileno
                || this.winnerRecord?.referraluser?.mobile
                || ''
        },
        addressValue() {
            const address = this.winnerRecord?.addressess?.[0]
                || this.winnerRecord?.address
                || this.winnerRecord?.addresses?.[0]

            if (!address) {
                return ''
            }

            if (typeof address === 'string') {
                return address
            }

            const city = address.city ? this.getMasterLabelByType(address.city, this.MASTER_CITIES) : ''
            const state = address.state ? this.getMasterLabelByType(address.state, this.MASTER_STATES) : ''

            return [address.houseno, address.street, address.landmark, city, state, address.zipcode]
                .filter(Boolean)
                .join(', ')
        },
        parentIdValue() {
            return this.winnerRecord?.referraluser?.generatedid
                || this.winnerRecord?.parentid
                || ''
        },
        parentNameValue() {
            return this.winnerRecord?.referraluser?.name
                || this.winnerRecord?.parentname
                || this.winnerRecord?.parent_name
                || ''
        },
        parentAddressValue() {
            const address = this.winnerRecord?.referraluser?.addressess?.[0]
                || this.winnerRecord?.referraluser?.address
                || this.winnerRecord?.referraluser?.addresses?.[0]
                || this.winnerRecord?.parentaddress
                || this.winnerRecord?.parent_address

            if (!address) {
                return ''
            }

            if (typeof address === 'string') {
                return address
            }

            const city = address.city ? this.getMasterLabelByType(address.city, this.MASTER_CITIES) : ''
            const state = address.state ? this.getMasterLabelByType(address.state, this.MASTER_STATES) : ''

            return [address.houseno, address.street, address.landmark, city, state, address.zipcode]
                .filter(Boolean)
                .join(', ')
        },
        saleRecord() {
            return this.payload?.sale
                || this.payload?.sale_record
                || this.payload?.salerecord
                || this.payload?.transaction
                || this.payload?.sale_details?.sale
                || this.payload?.sale_details?.transaction
                || this.payload?.sale_details
                || null
        },
        saleValue() {
            const value = this.saleRecord?.amtafterdiscount
                ?? this.saleRecord?.amountwithtax
                ?? this.saleRecord?.totalamount
                ?? this.saleRecord?.amount
            return value ?? 'N/A'
        },
        saleManualId() {
            return this.saleRecord?.loandetails?.manualid || 'N/A'
        },
        saleItems() {
            const items = Array.isArray(this.saleRecord?.items) ? this.saleRecord.items : []
            return items.map(item => ({
                id: item.id,
                productName: item.product?.name || item.productname || item.name || 'Item',
                quantity: item.quantity ?? item.qty ?? 0,
            }))
        },
    },
    mounted() {
        this.loadInitialPayload()
        this.startDraw()
    },
    beforeDestroy() {
        this.clearTimers()
    },
    methods: {
        loadInitialPayload() {
            if (this.flowType === 'sales' && this.winnerPayload) {
                this.payload = this.winnerPayload
            }
        },
        clearTimers() {
            if (this.shuffleTimer) {
                clearInterval(this.shuffleTimer)
                this.shuffleTimer = null
            }
            if (this.progressTimer) {
                clearInterval(this.progressTimer)
                this.progressTimer = null
            }
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer)
                this.countdownTimer = null
            }
        },
        startDraw() {
            this.clearTimers()
            this.winnerReady = false
            this.remark = ''
            this.saving = false
            this.saveSuccess = ''
            this.apiError = ''
            this.imageLoadError = false
            this.animationProgress = 0
            this.countdownSeconds = 10
            this.fetchedWinnerData = null
            this.statusText = 'Shuffling eligible candidate tickets across all entries...'

            // Rapid ticket shuffle ticker
            this.shuffleTimer = setInterval(() => {
                this.shuffleIndex = (this.shuffleIndex + 1) % this.mockCustomers.length
            }, 80)

            // Live 10-second countdown ticker
            this.countdownTimer = setInterval(() => {
                if (this.countdownSeconds > 1) {
                    this.countdownSeconds -= 1
                }
            }, 1000)

            // Progress bar simulation (10 seconds)
            const duration = 10000
            const interval = 40
            const step = (interval / duration) * 100

            this.progressTimer = setInterval(() => {
                this.animationProgress += step
                if (this.animationProgress >= 100) {
                    this.animationProgress = 100
                    this.finishDrawAnimation()
                }
            }, interval)

            // Fetch real winner API in background
            if (this.flowType === 'sales') {
                this.fetchRandomSaleWinner()
            } else {
                this.fetchRandomCustomerWinner()
            }
        },
        finishDrawAnimation() {
            this.clearTimers()
            if (this.fetchedWinnerData) {
                this.payload = this.fetchedWinnerData.payload
                this.position = this.fetchedWinnerData.position
                this.winnerReady = true
            } else if (this.apiError) {
                this.winnerReady = false
            } else {
                this.statusText = 'Finalizing grand winner reveal...'
                const waitInterval = setInterval(() => {
                    if (this.fetchedWinnerData) {
                        clearInterval(waitInterval)
                        this.payload = this.fetchedWinnerData.payload
                        this.position = this.fetchedWinnerData.position
                        this.winnerReady = true
                    } else if (this.apiError) {
                        clearInterval(waitInterval)
                        this.winnerReady = false
                    }
                }, 200)
            }
        },
        async fetchRandomCustomerWinner() {
            const param = this.searchParams || {}

            try {
                const response = await axios.post('api/users/lucky/winner', param)
                const winnerId = response.data?.id || response.data?.user_id

                if (!winnerId) {
                    throw new Error('No winner returned from lucky winner API.')
                }

                const userResponse = await axios.post('api/user/getuserbyid', { id: winnerId })
                const winner = userResponse.data

                this.fetchedWinnerData = {
                    payload: {
                        selecteduser: winner,
                        searchParams: param,
                        source: 'customer-search',
                    },
                    position: response.data?.position ?? null,
                }
            } catch (error) {
                console.log(error)
                this.apiError = error.response?.data?.message || 'Could not fetch a lucky winner from server.'
                this.statusText = 'Failed to load lucky winner.'
            }
        },
        async fetchRandomSaleWinner() {
            const param = this.searchParams || {}

            try {
                const response = await axios.post('api/sales/lucky/winner', param)
                const data = response.data?.data || response.data || {}
                const winner = data.selecteduser || data.customer || data.winner || data.user

                if (!winner) {
                    throw new Error(data.message || 'No eligible sale winner was returned.')
                }

                this.fetchedWinnerData = {
                    payload: {
                        ...data,
                        selecteduser: winner,
                        searchParams: param,
                        source: 'sales',
                    },
                    position: data.position ?? 1,
                }
            } catch (error) {
                console.log(error)
                this.apiError = error.response?.data?.message
                    || error.message
                    || 'Could not fetch a lucky winner from the filtered sales.'
                this.statusText = 'Failed to load sale winner.'
            }
        },
        formatRegistrationDate(value) {
            if (!value) {
                return ''
            }
            const parsed = moment(value)
            return parsed.isValid() ? parsed.format('DD-MM-YYYY') : value
        },
        saveLuckyWinnerRecord() {

            if (this.saving || this.saveSuccess) return

            if (!this.remark.trim()) {
                this.$notify({ text: 'Please enter a reward / remark note before saving.', type: 'warn' })
                return
            }

            if (this.remark.length > 255) {
                this.$notify({ text: 'Reward / remark must be 255 characters or fewer.', type: 'warn' })
                return
            }

            if (!this.winnerRecord?.id) {
                this.$notify({ text: 'Winner details are missing.', type: 'error' })
                return
            }

            this.saving = true
            const param = {
                customerid: this.winnerRecord.id,
                customername: this.winnerRecord.name,
                generatedid: this.winnerRecord.generatedid,
                rewards: this.remark,
                position: this.position ?? '1',
            }

            axios.post('api/lottery/record/save', param)
                .then((response) => {
                    if (response.data?.status !== true) {
                        throw new Error(response.data?.message || 'Unable to save winner record.')
                    }
                    this.saveSuccess = 'Winner reward saved successfully!'
                    this.$notify({ text: 'Winner reward saved successfully!', type: 'success' })
                    setTimeout(() => {
                        this.goBack()
                    }, 1500)
                })
                .catch(error => {
                    console.log(error)
                    this.$notify({ text: error.response?.data?.message || error.message || 'Unable to save winner record.', type: 'error' })
                })
                .finally(() => {
                    this.saving = false
                })
        },
        restart() {
            this.startDraw()
        },
        goBack() {
            this.clearTimers()
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
/* -------------------------------------------------------------
   FULLSCREEN STAGE SHELL (Edge-to-Edge, 100vw x 100vh)
   ------------------------------------------------------------- */
.lucky-fullscreen-stage {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    z-index: 99999;
    overflow: hidden;
    background:
        radial-gradient(ellipse at 50% 15%, rgba(255, 215, 0, 0.12) 0%, transparent 60%),
        radial-gradient(ellipse at 80% 80%, rgba(121, 40, 202, 0.12) 0%, transparent 50%),
        linear-gradient(145deg, #070c1e 0%, #050814 50%, #0d0a22 100%);
    box-sizing: border-box;
}

/* -------------------------------------------------------------
   STAGE VISUAL EFFECTS (Spotlights, Auroras, Particles)
   ------------------------------------------------------------- */
.stage-effects {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    z-index: 1;
}

.spotlight {
    position: absolute;
    opacity: 0.18;
    filter: blur(80px);
    pointer-events: none;
}

.spotlight-top {
    top: -160px;
    left: 50%;
    transform: translateX(-50%);
    width: 800px;
    height: 600px;
    background: radial-gradient(circle, rgba(255, 215, 0, 0.9) 0%, transparent 70%);
}

.spotlight-left {
    top: 10%;
    left: -100px;
    width: 500px;
    height: 800px;
    background: radial-gradient(circle, rgba(255, 170, 0, 0.8) 0%, transparent 70%);
}

.spotlight-right {
    top: 10%;
    right: -100px;
    width: 500px;
    height: 800px;
    background: radial-gradient(circle, rgba(0, 210, 255, 0.7) 0%, transparent 70%);
}

.aurora {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: 0.2;
}

.aurora-1 { width: 500px; height: 500px; top: -100px; left: 10%; background: #ffaa00; }
.aurora-2 { width: 550px; height: 550px; bottom: -100px; right: 10%; background: #7928ca; }
.aurora-3 { width: 400px; height: 400px; top: 40%; left: 50%; transform: translate(-50%, -50%); background: #0070f3; }

/* Balloons */
.balloon {
    position: absolute;
    width: 58px;
    height: 74px;
    border-radius: 50% 50% 48% 48%;
    opacity: 0;
    box-shadow: inset -8px -12px 18px rgba(0, 0, 0, 0.25);
    animation: floatBalloon 18s linear infinite;
}
.balloon::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -28px;
    width: 2px;
    height: 32px;
    background: rgba(255, 255, 255, 0.5);
    transform: translateX(-50%);
}
.balloon-1 { left: 4%; bottom: -90px; background: linear-gradient(180deg, #ff416c, #ff4b2b); animation-delay: 0s; }
.balloon-2 { right: 5%; bottom: -120px; background: linear-gradient(180deg, #f7b733, #fc4a1a); animation-delay: 4s; }
.balloon-3 { left: 88%; bottom: -150px; background: linear-gradient(180deg, #2193b0, #6dd5ed); animation-delay: 8s; }
.balloon-4 { left: 14%; bottom: -110px; background: linear-gradient(180deg, #b224ef, #7579ff); animation-delay: 5s; }

/* Stars */
.sparkle-star {
    position: absolute;
    color: #ffd700;
    opacity: 0;
    animation: twinkle 3s ease-in-out infinite;
}
.star-1 { top: 12%; left: 8%; font-size: 26px; animation-delay: 0.2s; }
.star-2 { top: 16%; right: 10%; font-size: 30px; animation-delay: 1.1s; }
.star-3 { bottom: 18%; left: 7%; font-size: 22px; animation-delay: 1.8s; }
.star-4 { bottom: 14%; right: 12%; font-size: 28px; animation-delay: 0.7s; }
.star-5 { top: 55%; left: 5%; font-size: 20px; animation-delay: 2.3s; }

/* Confetti Rain */
.confetti-wrapper {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
}
.confetti {
    position: absolute;
    top: -25px;
    width: 10px;
    height: 16px;
    opacity: 0.9;
    border-radius: 2px;
    animation: confettiFall 7s linear infinite;
}
.confetti-1 { left: 3%; background: #ffd700; animation-duration: 5.5s; }
.confetti-2 { left: 9%; background: #ff4757; animation-duration: 6.2s; animation-delay: 0.7s; }
.confetti-3 { left: 16%; background: #2ed573; animation-duration: 5.1s; animation-delay: 1.2s; }
.confetti-4 { left: 24%; background: #1e90ff; animation-duration: 6.8s; animation-delay: 0.3s; }
.confetti-5 { left: 31%; background: #ffa502; animation-duration: 5.8s; animation-delay: 1.5s; }
.confetti-6 { left: 38%; background: #e056fd; animation-duration: 5.3s; animation-delay: 0.9s; }
.confetti-7 { left: 45%; background: #ffd700; animation-duration: 6.1s; animation-delay: 0.2s; }
.confetti-8 { left: 52%; background: #ff6b81; animation-duration: 5.6s; animation-delay: 1.8s; }
.confetti-9 { left: 59%; background: #70a1ff; animation-duration: 6.4s; animation-delay: 0.5s; }
.confetti-10 { left: 66%; background: #7bed9f; animation-duration: 5.2s; animation-delay: 1.1s; }
.confetti-11 { left: 73%; background: #ffd700; animation-duration: 6.6s; animation-delay: 1.7s; }
.confetti-12 { left: 80%; background: #ff4757; animation-duration: 5.4s; animation-delay: 0.4s; }
.confetti-13 { left: 87%; background: #2ed573; animation-duration: 6.0s; animation-delay: 1.4s; }
.confetti-14 { left: 94%; background: #e056fd; animation-duration: 5.7s; animation-delay: 0.8s; }

/* -------------------------------------------------------------
   FULLSCREEN CONTAINER (Fits 100vh Perfectly without scroll)
   ------------------------------------------------------------- */
.fullscreen-container {
    position: relative;
    z-index: 10;
    width: 100%;
    height: 100%;
    padding: clamp(16px, 2.5vh, 28px) clamp(20px, 3.5vw, 48px);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: clamp(12px, 2vh, 20px);
}

/* -------------------------------------------------------------
   1. TOP EVENT HEADER BAR
   ------------------------------------------------------------- */
.event-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255, 215, 0, 0.2);
    flex-shrink: 0;
}

.event-brand-group {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.draw-date-range {
    flex-basis: 100%;
    margin: 0;
    color: #ffd700;
    font-size: clamp(14px, 1.6vw, 18px);
    font-weight: 700;
}

.draw-date-range i {
    margin-right: 8px;
}

.event-live-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    background: rgba(255, 215, 0, 0.12);
    border: 1px solid rgba(255, 215, 0, 0.4);
    border-radius: 999px;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.1em;
    color: #ffd700;
    box-shadow: 0 0 15px rgba(255, 215, 0, 0.25);
}

.live-pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #00ff88;
    box-shadow: 0 0 8px #00ff88;
    animation: livePulse 1.2s infinite;
}

.event-stage-title {
    margin: 0;
    font-size: clamp(22px, 2.6vw, 32px);
    font-weight: 900;
    letter-spacing: -0.01em;
    display: flex;
    align-items: center;
    gap: 12px;
}

.gold-gradient-text {
    background: linear-gradient(135deg, #fff385 0%, #ffd700 50%, #ff8800 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 2px 10px rgba(255, 191, 0, 0.4));
}

.stage-sub-badge {
    font-size: 14px;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 0.12em;
    padding: 4px 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.08);
}

.event-header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.stage-header-btn {
    border: 0;
    padding: 9px 20px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
}

.redraw-btn {
    background: rgba(255, 215, 0, 0.15);
    color: #ffd700;
    border: 1px solid rgba(255, 215, 0, 0.35);
}

.redraw-btn:hover {
    background: rgba(255, 215, 0, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.close-btn {
    width: 42px;
    height: 42px;
    padding: 0;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.15);
    font-size: 16px;
}

.close-btn:hover {
    background: rgba(255, 59, 48, 0.25);
    color: #ff3b30;
    border-color: rgba(255, 59, 48, 0.5);
    transform: rotate(90deg);
}

/* -------------------------------------------------------------
   2A. SUSPENSE DRAWING STAGE (Full-Screen Center Stage)
   ------------------------------------------------------------- */
.suspense-stage-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: clamp(18px, 3.5vh, 36px);
    text-align: center;
}

.lottery-drum-wrapper {
    position: relative;
    width: min(580px, 92vw);
    height: min(260px, 35vh);
    display: flex;
    align-items: center;
    justify-content: center;
}

.drum-orbit-ring {
    position: absolute;
    border-radius: 28px;
    pointer-events: none;
}

.ring-1 {
    inset: -12px;
    border: 2px solid rgba(255, 215, 0, 0.45);
    box-shadow: 0 0 35px rgba(255, 215, 0, 0.3);
    animation: pulseBorder 1.2s infinite alternate;
}

.ring-2 {
    inset: -6px;
    border: 1px dashed rgba(0, 210, 255, 0.6);
}

.ring-3 {
    inset: 0;
    border-radius: 22px;
    box-shadow: inset 0 0 30px rgba(255, 215, 0, 0.15);
}

.jackpot-reel-card {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 22px;
    background:
        radial-gradient(ellipse at center, rgba(255, 215, 0, 0.18) 0%, transparent 75%),
        linear-gradient(135deg, #121938 0%, #1e1242 100%);
    border: 2px solid rgba(255, 215, 0, 0.3);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 18px 26px;
    box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.7);
}

.reel-top-tag {
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.15em;
    color: #ffd700;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.jackpot-icon {
    font-size: 20px;
}

.rolling-name-container {
    height: 65px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.rolling-customer-name {
    margin: 0;
    font-size: clamp(32px, 4.5vw, 50px);
    font-weight: 900;
    color: #ffffff;
    letter-spacing: -0.01em;
    text-shadow: 0 0 22px rgba(255, 215, 0, 0.6);
    animation: shuffleJump 0.08s infinite alternate;
}

.rolling-id-badge {
    font-size: 17px;
    font-weight: 800;
    color: #00d2ff;
    letter-spacing: 0.1em;
    margin-top: 10px;
    background: rgba(0, 210, 255, 0.14);
    padding: 5px 18px;
    border-radius: 999px;
    border: 1px solid rgba(0, 210, 255, 0.35);
}

.suspense-bottom-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    width: 100%;
    max-width: 520px;
}

.suspense-countdown-badge {
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: #ffd700;
    background: rgba(255, 215, 0, 0.15);
    border: 1px solid rgba(255, 215, 0, 0.35);
    padding: 6px 18px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 0 15px rgba(255, 215, 0, 0.2);
}

.suspense-status-heading {
    margin: 0;
    font-size: clamp(22px, 2.6vw, 28px);
    font-weight: 800;
}

.shimmer-text {
    background: linear-gradient(90deg, #ffd700 0%, #ffffff 50%, #ffd700 100%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: textShimmer 2s infinite linear;
}

.event-progress-track {
    width: 100%;
    height: 10px;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 999px;
    overflow: hidden;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.5);
}

.event-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #ffd700, #ff8800, #ff3b30);
    border-radius: 999px;
    transition: width 0.05s linear;
    box-shadow: 0 0 16px rgba(255, 215, 0, 0.85);
}

.suspense-subtext {
    margin: 0;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.75);
}

/* -------------------------------------------------------------
   2B. WINNER REVEALED STAGE (Full-Screen Center Stage)
   ------------------------------------------------------------- */
.winner-revealed-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: clamp(14px, 2.2vh, 24px);
    animation: revealZoom 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

/* Prominent Winner Hero Showcase */
.winner-spotlight-card {
    position: relative;
    border-radius: 24px;
    padding: clamp(16px, 2.8vh, 26px) clamp(24px, 3.5vw, 40px);
    background:
        radial-gradient(ellipse at 15% 50%, rgba(255, 215, 0, 0.22) 0%, transparent 60%),
        linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(22, 28, 62, 0.9) 45%, rgba(45, 18, 75, 0.95) 100%);
    border: 2px solid rgba(255, 215, 0, 0.5);
    box-shadow:
        0 0 45px rgba(255, 215, 0, 0.22),
        0 20px 50px rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    gap: clamp(20px, 3vw, 36px);
    overflow: hidden;
}

.spotlight-aura {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent 45%, rgba(255, 255, 255, 0.12) 50%, transparent 55%);
    transform: rotate(25deg);
    animation: shimmerSlide 5s infinite linear;
    pointer-events: none;
}

.crown-badge-col {
    flex-shrink: 0;
}

.crown-orb {
    width: clamp(76px, 8.5vw, 100px);
    height: clamp(76px, 8.5vw, 100px);
    border-radius: 50%;
    background: linear-gradient(135deg, #ffd700 0%, #ff8800 100%);
    display: grid;
    place-items: center;
    box-shadow: 0 10px 30px rgba(255, 136, 0, 0.5);
    border: 3px solid #ffffff;
    animation: pulseBounce 2s infinite ease-in-out;
}

.crown-emoji {
    font-size: clamp(40px, 4.5vw, 54px);
    line-height: 1;
}

/* Winner Avatar Image Styling */
.winner-avatar-wrap {
    position: relative;
    width: clamp(76px, 8.5vw, 100px);
    height: clamp(76px, 8.5vw, 100px);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: pulseBounce 2s infinite ease-in-out;
}

.winner-avatar-img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 3.5px solid #ffd700;
    box-shadow: 0 0 25px rgba(255, 215, 0, 0.6), 0 8px 20px rgba(0, 0, 0, 0.5);
    background: #111827;
}

.avatar-crown-overlay {
    position: absolute;
    top: -14px;
    right: -6px;
    font-size: 28px;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.6));
    animation: crownFloat 2s ease-in-out infinite alternate;
}

@keyframes crownFloat {
    0% { transform: translateY(0) rotate(10deg); }
    100% { transform: translateY(-4px) rotate(18deg); }
}

.winner-info-col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.winner-headline-tags {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.grand-position-pill {
    padding: 6px 18px;
    background: linear-gradient(135deg, #ffd700 0%, #ff9900 100%);
    color: #101528;
    border-radius: 999px;
    font-weight: 900;
    font-size: 15px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    box-shadow: 0 4px 15px rgba(255, 153, 0, 0.4);
}

.grand-id-pill {
    padding: 6px 18px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 999px;
    font-weight: 800;
    font-size: 15px;
    color: #f1f5f9;
}

.grand-winner-name {
    margin: 4px 0;
    font-size: clamp(34px, 4.8vw, 56px);
    font-weight: 900;
    color: #ffffff;
    letter-spacing: -0.01em;
    line-height: 1.15;
    text-shadow:
        0 0 25px rgba(255, 215, 0, 0.5),
        0 4px 14px rgba(0, 0, 0, 0.6);
}

.winner-quick-pills {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
}

.contact-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 18px;
    background: rgba(0, 210, 255, 0.14);
    border: 1px solid rgba(0, 210, 255, 0.35);
    border-radius: 999px;
    color: #00d2ff;
    font-size: 16px;
    font-weight: 700;
}

.contact-pill.muted {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.18);
    color: rgba(255, 255, 255, 0.85);
}

/* Details Section 2-Column Split */
.details-section-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: clamp(14px, 2vw, 24px);
}

.span-full {
    grid-column: span 2;
}

.stage-info-card {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 20px;
    padding: clamp(14px, 2vh, 20px) clamp(16px, 2vw, 24px);
    backdrop-filter: blur(12px);
    transition: transform 0.2s ease, border-color 0.2s ease;
}

.stage-info-card:hover {
    border-color: rgba(255, 215, 0, 0.35);
}

.info-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 12px;
}

.info-card-icon {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: rgba(255, 215, 0, 0.15);
    color: #ffd700;
    display: grid;
    place-items: center;
    font-size: 17px;
}

.info-card-icon.parent-icon {
    background: rgba(0, 210, 255, 0.15);
    color: #00d2ff;
}

.info-card-header h3 {
    margin: 0;
    font-size: clamp(18px, 2.2vw, 22px);
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 0.03em;
}

.info-card-list {
    display: flex;
    flex-direction: column;
    gap: clamp(8px, 1.3vh, 12px);
}

.info-grid-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.02);
    font-size: clamp(15px, 1.6vw, 18px);
    gap: 14px;
}

.row-label {
    color: rgba(255, 255, 255, 0.7);
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
}

.row-label i {
    width: 16px;
    color: rgba(255, 215, 0, 0.75);
}

.row-value {
    color: #ffffff;
    font-weight: 700;
    text-align: right;
}

.gold-highlight { color: #ffd700; font-size: clamp(16px, 1.8vw, 20px); }
.cyan-highlight { color: #00d2ff; font-size: clamp(16px, 1.8vw, 20px); }

.text-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 320px;
}

/* Sale Strip */
.stage-sale-strip {
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 12px 20px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.sale-metric {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
}

.sale-metric .metric-lbl { color: rgba(255, 255, 255, 0.65); font-weight: 600; }
.sale-metric .metric-val { color: #ffffff; font-weight: 800; }
.sale-metric.gold-metric .metric-val { color: #ffd700; font-size: 18px; }

.sale-items-pill-group {
    display: flex;
    gap: 10px;
    margin-left: auto;
    flex-wrap: wrap;
}

.product-item-chip {
    padding: 5px 12px;
    border-radius: 8px;
    background: rgba(0, 210, 255, 0.12);
    border: 1px solid rgba(0, 210, 255, 0.25);
    font-size: 13px;
    color: #00d2ff;
    font-weight: 600;
}

/* -------------------------------------------------------------
   3. BOTTOM ACTION & REWARD BAR
   ------------------------------------------------------------- */
.stage-bottom-bar {
    display: flex;
    align-items: center;
    gap: 18px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    flex-shrink: 0;
}

.reward-entry-container {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 14px;
    background: rgba(10, 15, 32, 0.85);
    border: 1.5px solid rgba(255, 215, 0, 0.35);
    border-radius: 14px;
    padding: 8px 18px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.reward-entry-container:focus-within {
    border-color: #ffd700;
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
}

.reward-input-label {
    font-size: 16px;
    font-weight: 800;
    color: #ffd700;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 8px;
}

.stage-reward-input {
    flex: 1;
    background: transparent;
    border: 0;
    outline: none;
    color: #ffffff;
    font-size: 16px;
    padding: 8px 0;
}

.stage-reward-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
}

.stage-action-btns {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-shrink: 0;
}

.stage-btn {
    padding: 13px 26px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
    border: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.save-winner-btn {
    background: linear-gradient(135deg, #ffd700 0%, #ff8800 100%);
    color: #0a0e1c;
    box-shadow: 0 6px 20px rgba(255, 136, 0, 0.4);
}

.save-winner-btn:hover:not(:disabled) {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 10px 30px rgba(255, 136, 0, 0.55);
}

.redraw-stage-btn {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.redraw-stage-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
}

.stage-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

/* Toast & Error Elements */
.stage-toast {
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    padding: 14px 30px;
    border-radius: 12px;
    font-weight: 800;
    font-size: 16px;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    animation: toastPop 0.3s ease forwards;
}

.toast-success {
    background: rgba(0, 255, 136, 0.95);
    color: #07150e;
}

.toast-error,
.stage-error-banner {
    background: rgba(255, 59, 48, 0.95);
    color: #ffffff;
}

.stage-error-banner {
    padding: 12px 24px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.stage-retry-btn {
    background: #ffffff;
    color: #ff3b30;
    border: 0;
    padding: 5px 14px;
    border-radius: 6px;
    font-weight: 800;
    font-size: 14px;
    cursor: pointer;
}

/* -------------------------------------------------------------
   KEYFRAME ANIMATIONS
   ------------------------------------------------------------- */
@keyframes revealZoom {
    0% { opacity: 0; transform: scale(0.96) translateY(12px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes livePulse {
    0%, 100% { transform: scale(0.9); opacity: 0.7; }
    50% { transform: scale(1.3); opacity: 1; }
}

@keyframes pulseBounce {
    0%, 100% { transform: scale(0.95) rotate(-3deg); }
    50% { transform: scale(1.1) rotate(3deg); }
}

@keyframes pulseBorder {
    0% { opacity: 0.4; }
    100% { opacity: 0.9; }
}

@keyframes textShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
}

@keyframes shuffleJump {
    0% { transform: translateY(-2px); }
    100% { transform: translateY(2px); }
}

@keyframes shimmerSlide {
    0% { transform: translate(-100%, -100%) rotate(25deg); }
    100% { transform: translate(100%, 100%) rotate(25deg); }
}

@keyframes twinkle {
    0%, 100% { opacity: 0; transform: scale(0.6); }
    50% { opacity: 0.9; transform: scale(1.2); }
}

@keyframes floatBalloon {
    0% { transform: translate3d(0, 0, 0) rotate(-4deg); opacity: 0; }
    10% { opacity: 0.85; }
    100% { transform: translate3d(0, -140vh, 0) rotate(4deg); opacity: 0; }
}

@keyframes confettiFall {
    0% { transform: translateY(-20px) rotateX(0deg) rotateZ(0deg); opacity: 1; }
    100% { transform: translateY(105vh) rotateX(720deg) rotateZ(360deg); opacity: 0; }
}

@keyframes toastPop {
    0% { opacity: 0; transform: translate(-50%, 20px); }
    100% { opacity: 1; transform: translate(-50%, 0); }
}

/* -------------------------------------------------------------
   RESPONSIVE SCALING
   ------------------------------------------------------------- */
@media (max-width: 860px) {
    .details-section-grid {
        grid-template-columns: 1fr;
    }
    .span-full {
        grid-column: span 1;
    }
    .winner-spotlight-card {
        flex-direction: column;
        text-align: center;
    }
    .winner-info-col {
        align-items: center;
    }
    .stage-bottom-bar {
        flex-direction: column;
        align-items: stretch;
    }
    .stage-action-btns {
        justify-content: stretch;
    }
    .stage-btn {
        flex: 1;
        justify-content: center;
    }
}
</style>
